import {
  PUSH_NOTIFICATION_REGISTRY,
  PushDataKey,
  PushNotificationAction,
  PushTapTarget,
  WebPushDataKey,
  getPushNotificationConfig,
} from "@speakai/shared";
import {
  BODY_MAX_LENGTH,
  EVENT_ID_MAX_LENGTH,
  REMINDER_DASHBOARD_PATH,
  TITLE_MAX_LENGTH,
  cleanText,
  isRecord,
} from "./reminder-payload";

const ID_MAX_LENGTH = 128;

export interface EntityPush {
  type: string;
  tapTarget: PushTapTarget;
  title: string;
  body: string;
  tag: string;
  actions: PushNotificationAction[];
  path: string;
}

type PushIds = Partial<Record<PushDataKey, string>>;

const TAP_TARGET_PATHS: Record<PushTapTarget, (ids: PushIds) => string> = {
  [PushTapTarget.MEETING]: () => REMINDER_DASHBOARD_PATH,
  [PushTapTarget.MEDIA]: (ids) => `/media/${encodeURIComponent(ids[PushDataKey.MEDIA_ID] ?? "")}`,
  [PushTapTarget.RECORDER]: (ids) => `/recorder/${encodeURIComponent(ids[PushDataKey.RECORDER_ID] ?? "")}`,
};

export function resolveTapTargetPath(tapTarget: PushTapTarget, ids: PushIds): string {
  return TAP_TARGET_PATHS[tapTarget](ids);
}

function readId(raw: Record<string, unknown>, key: PushDataKey): string {
  const value = raw[key];
  return typeof value === "string" ? value.trim() : "";
}

export function parseEntityPush(raw: unknown): EntityPush | null {
  if (!isRecord(raw) || typeof raw.type !== "string") return null;
  const config = getPushNotificationConfig(raw.type as keyof typeof PUSH_NOTIFICATION_REGISTRY);
  if (!config || config.tapTarget === PushTapTarget.MEETING) return null;
  const ids: PushIds = {};
  for (const key of config.requiredDataKeys) {
    const value = readId(raw, key);
    if (!value || value.length > ID_MAX_LENGTH) return null;
    ids[key] = value;
  }
  const title = cleanText(raw[WebPushDataKey.TITLE], TITLE_MAX_LENGTH);
  if (!title) return null;
  const entityId = ids[PushDataKey.MEDIA_ID] ?? ids[PushDataKey.RECORDER_ID] ?? "";
  return {
    type: raw.type,
    tapTarget: config.tapTarget,
    title,
    body: cleanText(raw[WebPushDataKey.MESSAGE], BODY_MAX_LENGTH),
    tag: cleanText(raw[WebPushDataKey.TAG], EVENT_ID_MAX_LENGTH) || `${raw.type}:${entityId}`,
    actions: [],
    path: resolveTapTargetPath(config.tapTarget, ids),
  };
}
